import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Building2, Plus, Edit, Archive, Search, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';

const blankClient = {
  name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  abn: '',
  website: '',
  notes: '',
  default_currency: 'AUD',
  default_tax_rate: 0.10,
  default_payment_terms_days: 14,
};

export default function Clients() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankClient);
  const [saving, setSaving] = useState(false);
  const [summaries, setSummaries] = useState({});

  const canManage = canEdit(user, 'clients');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/clients', { params: { archived, search: search.trim() || undefined } });
      setClients(r.data || []);
      // Load summaries in parallel
      const sums = await Promise.all((r.data || []).map((c) => api.get(`/clients/${c.id}/summary`).then((s) => [c.id, s.data]).catch(() => [c.id, null])));
      setSummaries(Object.fromEntries(sums));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load clients');
    } finally {
      setLoading(false);
    }
  }, [archived, search]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      contact_name: c.contact_name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      abn: c.abn || '',
      website: c.website || '',
      notes: c.notes || '',
      default_currency: c.default_currency || 'AUD',
      default_tax_rate: c.default_tax_rate ?? 0.10,
      default_payment_terms_days: c.default_payment_terms_days ?? 14,
    });
  };

  const startCreate = () => {
    setEditing({ id: null });
    setForm(blankClient);
  };

  const close = () => {
    setEditing(null);
    setForm(blankClient);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/clients/${editing.id}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      close();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive ${c.name}?`)) return;
    try {
      await api.patch(`/clients/${c.id}`, { archived: true });
      toast.success('Client archived');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not archive');
    }
  };

  const unarchive = async (c) => {
    try {
      await api.patch(`/clients/${c.id}`, { archived: false });
      toast.success('Client restored');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore');
    }
  };

  const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Building2 className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                  {t('clients.title', { defaultValue: 'Clients' })}
                </h1>
                <p className="text-sm text-slate-500">
                  {t('clients.subtitle', { defaultValue: 'Companies you bill — link events, quotes and invoices to a client.' })}
                </p>
              </div>
            </div>
            {canManage && (
              <Button onClick={startCreate}>
                <Plus className="w-4 h-4 mr-1" />
                {t('clients.new', { defaultValue: 'New client' })}
              </Button>
            )}
          </div>

          <Card className="glass-card p-4 mb-4">
            <div className="flex gap-3 flex-wrap items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, contact, email…"
                    className="pl-8"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
                Show archived
              </label>
            </div>
          </Card>

          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading…</p>
          ) : clients.length === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>{archived ? 'No archived clients.' : 'No clients yet — create your first one to start sending quotes and invoices.'}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((c) => {
                const s = summaries[c.id];
                return (
                  <Card key={c.id} className="glass-card p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                        {c.contact_name && <p className="text-xs text-slate-500 truncate">{c.contact_name}</p>}
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-emerald-600 p-1" title="Edit"><Edit className="w-3.5 h-3.5" /></button>
                          {c.archived
                            ? <button onClick={() => unarchive(c)} className="text-slate-400 hover:text-emerald-600 text-xs px-1" title="Restore">↩</button>
                            : <button onClick={() => archive(c)} className="text-slate-400 hover:text-red-600 p-1" title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                          }
                        </div>
                      )}
                    </div>
                    {c.email && <p className="text-xs text-slate-600 truncate">📧 {c.email}</p>}
                    {c.phone && <p className="text-xs text-slate-600">📞 {c.phone}</p>}
                    {c.abn && <p className="text-xs text-slate-500 mt-1">ABN: {c.abn}</p>}
                    {s && (
                      <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <p className="text-slate-400">Events</p>
                          <p className="font-semibold text-slate-700">{s.events_count}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Invoiced</p>
                          <p className="font-semibold text-emerald-700">{fmtMoney(s.invoiced_total, c.default_currency)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Outstanding</p>
                          <p className="font-semibold text-amber-700">{fmtMoney(s.outstanding_total, c.default_currency)}</p>
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex gap-1.5 flex-wrap">
                      <Link to={`/quotes?client_id=${c.id}`} className="text-[11px] text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-0.5">
                        <ExternalLink className="w-3 h-3" /> Quotes
                      </Link>
                      <Link to={`/invoices?client_id=${c.id}`} className="text-[11px] text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-0.5">
                        <ExternalLink className="w-3 h-3" /> Invoices
                      </Link>
                      <Link to={`/events?client_id=${c.id}`} className="text-[11px] text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-0.5">
                        <ExternalLink className="w-3 h-3" /> Events
                      </Link>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit client' : 'New client'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Contact name</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label>ABN / Tax ID</Label>
              <Input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
            </div>
            <div>
              <Label>Currency</Label>
              <Input value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>Default tax rate</Label>
              <Input type="number" step="0.01" value={form.default_tax_rate} onChange={(e) => setForm({ ...form, default_tax_rate: parseFloat(e.target.value) || 0 })} />
              <p className="text-[10px] text-slate-400 mt-0.5">0.10 = 10% GST</p>
            </div>
            <div>
              <Label>Payment terms (days)</Label>
              <Input type="number" value={form.default_payment_terms_days} onChange={(e) => setForm({ ...form, default_payment_terms_days: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
